import React, { useEffect, useState } from 'react';
import { Snackbar, Box,
  Card,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  IconButton,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  InputAdornment, } from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { User, usersAPI, Company, companiesAPI } from '../../services/api';

const AVAILABLE_PERMISSIONS = [
  { id: 'users', label: 'مدیریت کاربران' },
  { id: 'meals', label: 'مدیریت غذاها' },
  { id: 'menu', label: 'برنامه غذایی' },
  { id: 'reservations', label: 'رزروها' },
  { id: 'guest-reservations', label: 'رزرو مهمان' },
  { id: 'personnel-reservations', label: 'ثبت رزرو پرسنل' },
  { id: 'reports', label: 'گزارش‌گیری' },
  { id: 'monitor', label: 'مانیتورینگ رستوران' },
  { id: 'managers', label: 'مدیریت مدیران' },
  { id: 'delivery-groups', label: 'تحویل گروهی' },
  { id: 'settings', label: 'تنظیمات سامانه' },
];

const AdminManagers: React.FC = () => {
  const [managers, setManagers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    mobile: '',
    password: '',
    permissions: [] as string[],
    companyId: '',
    role: 'manager' as 'manager' | 'admin' | 'superadmin',
  });
  const [companies, setCompanies] = useState<Company[]>([]);
  const [editMode, setEditMode] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadManagers();
    loadCompanies();
  }, []);

  const loadCompanies = async () => {
    try {
      const data = await companiesAPI.getCompanies();
      setCompanies(data);
    } catch (err) {
      console.error('Failed to load companies');
    }
  };

  const loadManagers = async () => {
    try {
      setLoading(true);
      const data = await usersAPI.getUsers('admin,manager');
      setManagers(data);
    } catch (err: any) {
      setError('خطا در بارگذاری مدیران');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      if (editMode && editId) {
        await usersAPI.updateUser(editId, {
          ...formData,
          role: formData.role,
        });
        setSuccess('کاربر با موفقیت ویرایش شد');
      } else {
        await usersAPI.createUser({
          ...formData,
          role: formData.role,
        });
        setSuccess('کاربر با موفقیت اضافه شد');
      }
      setOpenModal(false);
      resetForm();
      loadManagers();
    } catch (err: any) {
      console.error(err);
      setError(err.response?.data?.error || (editMode ? 'خطا در ویرایش مدیر' : 'خطا در اضافه کردن مدیر'));
    }
  };

  const resetForm = () => {
    setFormData({
      firstName: '',
      lastName: '',
      mobile: '',
      password: '',
      permissions: [],
      companyId: '',
      role: 'manager',
    });
    setEditMode(false);
    setEditId(null);
  };

  const handleEdit = (manager: User) => {
    setFormData({
      firstName: manager.firstName,
      lastName: manager.lastName,
      mobile: manager.mobile,
      password: '', // Password is empty for security, optional in update
      permissions: (manager.role === 'admin' || manager.role === 'superadmin') 
        ? AVAILABLE_PERMISSIONS.map(p => p.id) 
        : (manager.permissions || []),
      companyId: manager.companyId || '',
      role: manager.role as 'manager' | 'admin' | 'superadmin',
    });
    setEditMode(true);
    setEditId(manager.id);
    setOpenModal(true);
  };

  const handleDelete = async (managerId: string) => {
    if (!window.confirm('آیا از حذف این مدیر مطمئن هستید؟')) return;

    try {
      await usersAPI.deleteUser(managerId);
      setSuccess('مدیر با موفقیت حذف شد');
      loadManagers();
    } catch (err: any) {
      setError('خطا در حذف مدیر');
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 4,
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontFamily: 'Vazir, sans-serif',
            fontWeight: 600,
          }}
        >
          کاربران سامانه
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            resetForm();
            setOpenModal(true);
          }}
          sx={{ fontFamily: 'Vazir, sans-serif' }}
        >
          افزودن مدیر
        </Button>
      </Box>

      <Snackbar open={!!error} autoHideDuration={2000} onClose={() => { if(typeof setError === 'function') setError(''); }} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
  <Alert severity="error" sx={{ width: '100%', fontFamily: 'Vazir, sans-serif' }} onClose={() => { if(typeof setError === 'function') setError(''); }}>
    {error}
  </Alert>
</Snackbar>
      <Snackbar open={!!success} autoHideDuration={2000} onClose={() => setSuccess('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
  <Alert severity="success" sx={{ width: '100%', fontFamily: 'Vazir, sans-serif' }} onClose={() => setSuccess('')}>
    {success}
  </Alert>
</Snackbar>

      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 600 }}>
                  نام
                </TableCell>
                <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 600 }}>
                  نقش
                </TableCell>
                <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 600 }}>
                  نام خانوادگی
                </TableCell>
                <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 600 }}>
                  موبایل
                </TableCell>
                <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 600 }}>
                  عملیات
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {managers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontFamily: 'Vazir, sans-serif' }}
                    >
                      هیچ مدیری یافت نشد
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                managers.map((manager) => (
                  <TableRow key={manager.id} hover>
                    <TableCell sx={{ fontFamily: 'Vazir, sans-serif' }}>
                      {manager.firstName}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'Vazir, sans-serif' }}>
                      {manager.role === 'superadmin' ? 'مدیر ارشد' : manager.role === 'admin' ? 'ادمین' : 'مدیر'}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'Vazir, sans-serif' }}>
                      {manager.lastName}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'Vazir, sans-serif' }}>
                      {manager.mobile} <br />
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Vazir, sans-serif' }}>
                        {manager.companyTitle ? `شرکت: ${manager.companyTitle}` : 'دسترسی همگانی'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <IconButton
                        color="primary"
                        size="small"
                        onClick={() => handleEdit(manager)}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        color="error"
                        size="small"
                        onClick={() => handleDelete(manager.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      <Dialog
        open={openModal}
        onClose={() => setOpenModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 600 }}>
          {editMode ? 'ویرایش مدیر' : 'افزودن مدیر'}
        </DialogTitle>
        <Box component="form" onSubmit={handleSubmit}>
          <DialogContent>
            <TextField
              fullWidth
              label="نام"
              value={formData.firstName}
              onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
              required
              sx={{ mb: 2 }}
              InputLabelProps={{
                sx: { fontFamily: 'Vazir, sans-serif' },
              }}
              inputProps={{
                sx: { fontFamily: 'Vazir, sans-serif' },
              }}
            />
            <TextField
              fullWidth
              label="نام خانوادگی"
              value={formData.lastName}
              onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
              required
              sx={{ mb: 2 }}
              InputLabelProps={{
                sx: { fontFamily: 'Vazir, sans-serif' },
              }}
              inputProps={{
                sx: { fontFamily: 'Vazir, sans-serif' },
              }}
            />
            <TextField
              fullWidth
              label="شماره موبایل"
              type="tel"
              value={formData.mobile}
              onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
              required
              sx={{ mb: 2 }}
              autoComplete="off"
              InputLabelProps={{
                sx: { fontFamily: 'Vazir, sans-serif' },
              }}
              inputProps={{
                sx: { fontFamily: 'Vazir, sans-serif' },
                pattern: '09[0-9]{9}',
              }}
            />
            <TextField
              fullWidth
              label="رمز عبور"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              required={!editMode}
              sx={{ mb: 2 }}
              autoComplete="new-password"
              InputLabelProps={{
                sx: { fontFamily: 'Vazir, sans-serif' },
              }}
              inputProps={{
                sx: { fontFamily: 'Vazir, sans-serif' },
                minLength: 6,
                placeholder: editMode ? 'تغییر رمز عبور (اختیاری)' : '',
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="نمایش رمز عبور"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="companyId-label" sx={{ fontFamily: 'Vazir, sans-serif' }}>محدودیت دسترسی پرسنل (شرکت)</InputLabel>
              <Select
                labelId="companyId-label"
                value={formData.companyId}
                label="محدودیت دسترسی پرسنل (شرکت)"
                onChange={(e) => setFormData({ ...formData, companyId: e.target.value })}
                sx={{ fontFamily: 'Vazir, sans-serif' }}
              >
                <MenuItem value="" sx={{ fontFamily: 'Vazir, sans-serif' }}>
                  <em>بدون محدودیت (دسترسی همگانی)</em>
                </MenuItem>
                {companies.map((company) => (
                  <MenuItem key={company.id} value={company.id} sx={{ fontFamily: 'Vazir, sans-serif' }}>
                    {company.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="role-label" sx={{ fontFamily: 'Vazir, sans-serif' }}>نقش کاربر</InputLabel>
              <Select
                labelId="role-label"
                value={formData.role}
                label="نقش کاربر"
                onChange={(e) => setFormData({ ...formData, role: e.target.value as any })}
                sx={{ fontFamily: 'Vazir, sans-serif' }}
              >
                <MenuItem value="manager" sx={{ fontFamily: 'Vazir, sans-serif' }}>مدیر</MenuItem>
                <MenuItem value="admin" sx={{ fontFamily: 'Vazir, sans-serif' }}>ادمین</MenuItem>
              </Select>
            </FormControl>

            <Typography variant="subtitle2" sx={{ mb: 1, mt: 2, fontFamily: 'Vazir, sans-serif' }}>
              دسترسی‌ها:
              {(formData.role === 'admin' || formData.role === 'superadmin') && (
                <span style={{ fontSize: '0.8rem', color: '#666', marginRight: '8px' }}>
                  (ادمین‌ها به همه بخش‌ها دسترسی دارند)
                </span>
              )}
            </Typography>
            <FormGroup>
              {AVAILABLE_PERMISSIONS.map((perm) => {
                const isAdmin = formData.role === 'admin' || formData.role === 'superadmin';
                return (
                  <FormControlLabel
                    key={perm.id}
                    control={
                      <Checkbox
                        checked={isAdmin ? true : formData.permissions.includes(perm.id)}
                        disabled={isAdmin}
                        onChange={(e) => {
                          if (isAdmin) return;
                          const newPermissions = e.target.checked
                            ? [...formData.permissions, perm.id]
                            : formData.permissions.filter(p => p !== perm.id);
                          setFormData({ ...formData, permissions: newPermissions });
                        }}
                      />
                    }
                    label={<span style={{ fontFamily: 'Vazir, sans-serif' }}>{perm.label}</span>}
                  />
                );
              })}
            </FormGroup>

          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={() => setOpenModal(false)}
              sx={{ fontFamily: 'Vazir, sans-serif' }}
            >
              انصراف
            </Button>
            <Button
              type="submit"
              variant="contained"
              sx={{ fontFamily: 'Vazir, sans-serif' }}
            >
              ذخیره
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </Box>
  );
};

export default AdminManagers;
